import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import { 
  LayoutDashboard, 
  FolderKanban, 
  FileText, 
  Package, 
  Users, 
  LogOut, 
  Menu, 
  X,
  Key,
  User,
  Tag,
  Cloud,
  Zap,
  Lightbulb,
  Ticket,
  History,
  FileCode2,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = config.BACKEND_URL;

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/projects', label: 'Projects', icon: FolderKanban },
  { path: '/user-stories', label: 'User Stories', icon: FileText },
  { path: '/ideas', label: 'Ideas & Wishes', icon: Lightbulb },
  { path: '/tickets', label: 'Tickets', icon: Ticket },
  { path: '/releases', label: 'Releases', icon: Package },
  { path: '/documents', label: 'Documents', icon: Cloud },
];

const adminItems = [
  { path: '/admin/users', label: 'Manage Users', icon: Users },
  { path: '/admin/projects', label: 'Manage Projects', icon: FolderKanban },
  { path: '/admin/statuses', label: 'Story Statuses', icon: Tag },
  { path: '/admin/api-tokens', label: 'API Tokens', icon: Key },
  { path: '/admin/actions', label: 'Actions', icon: Zap },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: History },
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apiDocsEnabled, setApiDocsEnabled] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(
    () => localStorage.getItem('adminMenuExpanded') === 'true'
  );

  const toggleAdminExpanded = () => {
    setAdminExpanded((prev) => {
      const next = !prev;
      localStorage.setItem('adminMenuExpanded', String(next));
      return next;
    });
  };

  useEffect(() => {
    // Check if API docs are enabled
    axios.get(`${API}/api/config/public`)
      .then(res => setApiDocsEnabled(res.data.api_docs_enabled))
      .catch(() => setApiDocsEnabled(false));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-border">
            <h1 className="text-xl font-semibold text-foreground">{config.SITE_TITLE}</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {isAdmin() && (
              <>
                <button
                  type="button"
                  onClick={toggleAdminExpanded}
                  className="mt-8 mb-3 px-4 flex items-center justify-between w-full text-left"
                  data-testid="admin-menu-toggle"
                  aria-expanded={adminExpanded}
                >
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Admin
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${adminExpanded ? '' : '-rotate-90'}`}
                  />
                </button>
                {adminExpanded && (
                  <div className="space-y-1">
                    {adminItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`sidebar-link ${isActive ? 'active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-auto py-2"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {user ? getInitials(user.name) : '??'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground capitalize">{user?.role}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content min-h-screen">
        {/* Top Header Bar - only show if API docs enabled */}
        {apiDocsEnabled && (
          <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
            <div className="flex items-center justify-end px-6 py-3 max-w-7xl mx-auto">
              <a
                href={`${config.BACKEND_URL}/api/docs`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="swagger-ui-link"
              >
                <Button variant="outline" size="sm" className="gap-2">
                  <FileCode2 className="h-4 w-4" />
                  API Docs
                </Button>
              </a>
            </div>
          </div>
        )}
        <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto w-full">
          {children}
        </div>
      </main>
    </div>
  );
};
